/**
 * On-device store — IndexedDB handle and account namespacing
 *
 * Everything stored here is a cache of server truth, so nothing in this file
 * is precious: losing the database costs a re-sync, never user data. The one
 * exception lives elsewhere (`playbackSyncQueue.js`), which owns the pending
 * writes that must survive.
 *
 * Every key is namespaced by the account's `sub` claim so two accounts sharing
 * a browser cannot read each other's library (FR-027). This mirrors the
 * existing `snapshotKey(sub, fileId)` convention in `apiPlayedChapters.js`.
 *
 * See specs/013-offline-first-pwa/data-model.md for the schema.
 */

import { deleteDB } from 'idb';
import { openDbSafely } from './idb';

export const SCHEMA_VERSION = 1;

export const dbName = (sub) => `dissona::${sub}`;
export const audioCacheName = (sub) => `dissona-audio::${sub}`;

/**
 * Open (and migrate) the account's database.
 * @param {string} sub - the account's `sub` claim
 * @param {(why: string) => void} [onLost] - called if the connection is given
 *   up (another tab is upgrading, or the browser closed it); the handle is
 *   dead from that point and the caller must reopen. See `./idb`.
 */
export const openAccountDb = (sub, onLost) => {
  if (!sub) throw new Error('openAccountDb: sub is required');
  return openDbSafely(dbName(sub), SCHEMA_VERSION, {
    onLost,
    upgrade(db) {
      if (!db.objectStoreNames.contains('docs')) {
        const docs = db.createObjectStore('docs', { keyPath: 'id' });
        docs.createIndex('by_project_type', ['projectId', 'type']);
      }
      if (!db.objectStoreNames.contains('downloads')) {
        db.createObjectStore('downloads', { keyPath: 'chapterId' });
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta');
      }
    },
  });
};

/**
 * Delete everything belonging to one account: documents, download index, sync
 * state and cached audio (FR-026, FR-027).
 *
 * Non-blocking by contract — this runs from the sign-out path, which must not
 * be held up or broken by a storage failure.
 */
export const wipeAccount = async (sub) => {
  if (!sub) return;
  try {
    await deleteDB(dbName(sub));
  } catch (e) {
    console.warn('wipeAccount: deleteDB failed', e);
  }
  try {
    if (typeof caches !== 'undefined') await caches.delete(audioCacheName(sub));
  } catch (e) {
    console.warn('wipeAccount: cache delete failed', e);
  }
};
